import { useEffect, useState, useMemo, useCallback } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { scaleQuantile } from 'd3-scale';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { RefreshCw, MapPin } from 'lucide-react';
import { logger } from '../utils/logger';
import { US_STATE_BY_CODE } from '../data/usStates';

const API_URL = process.env.REACT_APP_BACKEND_URL;
// US states TopoJSON (public CDN). 2-letter state postal code in properties.postal/id.
const GEO_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';

// Map FIPS code → 2-letter postal code (react-simple-maps + us-atlas uses FIPS)
const FIPS_TO_POSTAL = {
  '01': 'AL', '02': 'AK', '04': 'AZ', '05': 'AR', '06': 'CA', '08': 'CO', '09': 'CT',
  '10': 'DE', '11': 'DC', '12': 'FL', '13': 'GA', '15': 'HI', '16': 'ID', '17': 'IL',
  '18': 'IN', '19': 'IA', '20': 'KS', '21': 'KY', '22': 'LA', '23': 'ME', '24': 'MD',
  '25': 'MA', '26': 'MI', '27': 'MN', '28': 'MS', '29': 'MO', '30': 'MT', '31': 'NE',
  '32': 'NV', '33': 'NH', '34': 'NJ', '35': 'NM', '36': 'NY', '37': 'NC', '38': 'ND',
  '39': 'OH', '40': 'OK', '41': 'OR', '42': 'PA', '44': 'RI', '45': 'SC', '46': 'SD',
  '47': 'TN', '48': 'TX', '49': 'UT', '50': 'VT', '51': 'VA', '53': 'WA', '54': 'WV',
  '55': 'WI', '56': 'WY'
};

const RANGES = [
  { key: 'all', label: 'All Time', days: null },
  { key: 'weekly', label: 'Last 7 days', days: 7 },
  { key: 'monthly', label: 'Last 30 days', days: 30 },
  { key: 'quarterly', label: 'Last 90 days', days: 90 },
  { key: 'year', label: 'Last 365 days', days: 365 },
];

export default function LeadsByStateMap() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rangeKey, setRangeKey] = useState('all');
  const [hovered, setHovered] = useState(null);

  const isoDaysAgo = useCallback((days) => {
    const d = new Date();
    d.setDate(d.getDate() - days + 1);
    return d.toISOString().split('T')[0];
  }, []);

  const fetchMap = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      const range = RANGES.find(r => r.key === rangeKey);
      if (range?.days) {
        params.append('start_date', isoDaysAgo(range.days));
        params.append('end_date', new Date().toISOString().split('T')[0]);
      }
      const res = await fetch(`${API_URL}/api/intelligence/leads-by-state?${params}`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } catch (err) {
      logger.error('Leads-by-state map fetch error', err);
    } finally {
      setLoading(false);
    }
  }, [rangeKey, isoDaysAgo]);

  useEffect(() => { fetchMap(); }, [fetchMap]);

  const stateMap = useMemo(() => {
    const m = {};
    (data?.by_state || []).forEach(s => { m[s.state_code] = s; });
    return m;
  }, [data]);

  // Color scale: light → dark navy based on lead count.
  const colorScale = useMemo(() => {
    const counts = (data?.by_state || []).map(s => s.count);
    if (counts.length === 0) return () => '#f1f5f9';
    return scaleQuantile()
      .domain(counts)
      .range(['#dbeafe', '#93c5fd', '#3b82f6', '#1d4ed8', '#1e3a8a']);
  }, [data]);

  const top5 = (data?.by_state || []).slice(0, 5);

  return (
    <div className="bg-white rounded-xl border border-slate-100 p-5" data-testid="leads-by-state-map">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-blue-500" />
          <div>
            <h4 className="text-sm font-semibold text-slate-900">Leads by US State</h4>
            <p className="text-xs text-slate-500 mt-0.5">
              {data?.total_leads_with_state ?? 0} lead{(data?.total_leads_with_state ?? 0) === 1 ? '' : 's'} with a recorded state
              {data?.by_state?.length ? ` · ${data.by_state.length} state${data.by_state.length === 1 ? '' : 's'}` : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={rangeKey} onValueChange={setRangeKey}>
            <SelectTrigger className="h-8 w-[140px] text-xs" data-testid="map-range-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANGES.map(r => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <button onClick={fetchMap} disabled={loading} className="p-1.5 rounded-md hover:bg-slate-50 text-slate-400" data-testid="map-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Map */}
        <div className="lg:col-span-2 relative">
          <ComposableMap projection="geoAlbersUsa" projectionConfig={{ scale: 900 }} width={780} height={420} style={{ width: '100%', height: 'auto' }}>
            <Geographies geography={GEO_URL}>
              {({ geographies }) =>
                geographies.map(geo => {
                  const fips = String(geo.id).padStart(2, '0');
                  const postal = FIPS_TO_POSTAL[fips];
                  const stateEntry = stateMap[postal];
                  const fill = stateEntry ? colorScale(stateEntry.count) : '#f1f5f9';
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill={fill}
                      stroke="#ffffff"
                      strokeWidth={0.6}
                      style={{
                        default: { outline: 'none', transition: 'fill 0.15s' },
                        hover: { fill: '#f59e0b', outline: 'none', cursor: 'pointer' },
                        pressed: { outline: 'none' },
                      }}
                      onMouseEnter={() => setHovered({
                        postal,
                        name: US_STATE_BY_CODE[postal] || postal,
                        count: stateEntry?.count || 0,
                        percentage: stateEntry?.percentage || 0,
                      })}
                      onMouseLeave={() => setHovered(null)}
                      data-testid={`map-state-${postal || fips}`}
                    />
                  );
                })
              }
            </Geographies>
          </ComposableMap>
          {hovered && (
            <div className="absolute top-2 right-2 bg-slate-900 text-white rounded-lg px-3 py-2 text-xs shadow-lg pointer-events-none" data-testid="map-tooltip">
              <div className="font-semibold">{hovered.name}</div>
              <div className="text-slate-300">{hovered.count} leads {hovered.count > 0 && `(${hovered.percentage}%)`}</div>
            </div>
          )}
          <div className="flex items-center justify-center gap-2 mt-2">
            <span className="text-[10px] text-slate-400 uppercase tracking-wider">Fewer</span>
            <div className="flex">
              {['#dbeafe', '#93c5fd', '#3b82f6', '#1d4ed8', '#1e3a8a'].map(c => (
                <div key={c} className="w-6 h-2" style={{ backgroundColor: c }} />
              ))}
            </div>
            <span className="text-[10px] text-slate-400 uppercase tracking-wider">More</span>
          </div>
        </div>

        {/* Top 5 States */}
        <div>
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Top States</p>
          {top5.length === 0 ? (
            <div className="text-sm text-slate-400 italic py-8 text-center">No leads have a recorded US state yet.</div>
          ) : (
            <div className="space-y-2">
              {top5.map((s, i) => (
                <div key={s.state_code} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2" data-testid={`map-top-state-${s.state_code}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-xs font-bold text-slate-400 w-4">{i + 1}</span>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-900 truncate">{US_STATE_BY_CODE[s.state_code] || s.state_code}</div>
                      <div className="text-[10px] text-slate-400">{s.state_code}</div>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-sm font-bold text-slate-900 tabular-nums">{s.count}</div>
                    <div className="text-[10px] text-slate-400 tabular-nums">{s.percentage}%</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
